import { useCallback } from 'react';
import styled from 'styled-components';
import {
  useRfqFiltersModalState,
  useRfqFiltersModalMethods,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-filters/modal-context';
import {
  blueCharcoal,
  pickledBluewood,
} from '@paradigm/design-system/src/colors';
import {
  useRfqFiltersFormActions,
  useRfqFiltersFormView,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-filters/form-context';
import { useRfqFiltersValue } from '@paradigm/features/src/unified-rfqs/ui/rfq-filters/filter-provider';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import Button from '@paradigm/design-system/src/components/Button';
import HeadedModal from '@paradigm/design-system/src/components/HeadedModal';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { useTrackAction } from '@paradigm/features/src/analytics';

interface Props {
  formContent: React.ReactNode;
}

export default function RfqFilterModal({ formContent }: Props) {
  const { isOpen } = useRfqFiltersModalState();
  const { close } = useRfqFiltersModalMethods();
  const { clearValues, hidrate } = useRfqFiltersFormActions();
  const isMobile = useIsMobile();
  const view = useRfqFiltersFormView();
  const {
    assets,
    auctionTypes,
    instruments,
    venues,
    products,
    strategies,
    counterparties,
  } = view;
  const { submitFilter, state } = useRfqFiltersValue();
  const track = useTrackAction();

  const closeSyncFiltersState = useCallback(() => {
    close();
    hidrate(state);
  }, [state, close, hidrate]);

  const onClearValues = useCallback(() => {
    clearValues();
    track('clear filters');
  }, [clearValues, track]);

  const onSubmit: typeof submitFilter = useCallback(() => {
    submitFilter();
    track('apply filters', { filters: view });
  }, [submitFilter, track, view]);

  const onClose = useCallback(() => {
    closeSyncFiltersState();
    track('close modal');
  }, [closeSyncFiltersState, track]);

  const numOfFiltersSelected =
    assets.selectedOptions.length +
    auctionTypes.selectedOptions.length +
    instruments.selectedOptions.length +
    venues.selectedOptions.length +
    products.selectedOptions.length +
    strategies.selectedOptions.length +
    counterparties.selectedOptions.length;

  return (
    <HeadedModal
      isOpen={isOpen}
      width={530}
      title="Filters"
      titlePlacement={isMobile ? 'center' : 'left'}
      theme={isMobile ? 'primary-dark' : 'secondary-light'}
      titleCss="font-weight: normal;"
      close={onClose}
      data-testid="rfq_filters_modal"
      footerCss="justify-content: space-between;"
      footerContent={
        <>
          <Button
            ariaLabel="clear all filters"
            size="large"
            onClick={onClearValues}
            variant="link"
          >
            Clear All
          </Button>
          <Button size="large" ariaLabel="submit filter" onClick={onSubmit}>
            {numOfFiltersSelected === 0 ? 'Show All' : `Filter`}
          </Button>
        </>
      }
    >
      <Body>{formContent}</Body>
    </HeadedModal>
  );
}

export const Footer = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  border-top: 1px solid ${pickledBluewood};
  justify-content: space-between;
  padding: 0 16px;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    background-color: ${blueCharcoal};
    border-top: 1px solid transparent;
    button {
      font-size: 1rem;
    }
    padding: 0 1.5rem;
  }
`;

export const Body = styled.div`
  padding: 1rem;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    background-color: ${blueCharcoal};
    padding: 1.5rem 1.5rem 0;
  }
`;
